import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import getGuestCart from '../service/getGuestCart';
import {
  GuestCartContextType,
  GuestCartItem,
  GuestCartResponse,
} from '../interface/interface';

const GuestCartContext = createContext<GuestCartContextType | undefined>(
  undefined,
);

export const useGuestCart = () => {
  const context = useContext(GuestCartContext);
  if (!context) {
    throw new Error('useGuestCart must be used within a GuestCartProvider');
  }
  return context;
};

interface GuestCartProviderProps {
  children: ReactNode;
}

export const GuestCartProvider: React.FC<GuestCartProviderProps> = ({
  children,
}) => {
  const [cartItems, setCartItems] = useState<GuestCartItem[]>([]);
  const [cartCount, setCartCount] = useState<number>(0);

  const refreshCart = async () => {
    try {
      // Check if cart ID exists first
      const guestCartId = sessionStorage.getItem('guestCartId');
      if (!guestCartId) {
        setCartItems([]);
        setCartCount(0);
        return;
      }

      const cartResponse: GuestCartResponse = await getGuestCart();

      if (cartResponse?.success && cartResponse.cart?.line_items) {
        const items: GuestCartItem[] = cartResponse.cart.line_items.map(
          (item: any) => ({
            product_id: item.product_id,
            quantity: item.quantity || 0,
          }),
        );

        setCartItems(items);
        setCartCount(items.reduce((total, item) => total + item.quantity, 0));

        // Update sessionStorage with current cart items for FloatingCart to use
        sessionStorage.setItem('guestCart', JSON.stringify(items));
      } else {
        setCartItems([]);
        setCartCount(0);
        sessionStorage.removeItem('guestCart');
      }
    } catch (error) {
      console.error('Error refreshing guest cart:', error);
      setCartItems([]);
      setCartCount(0);
      sessionStorage.removeItem('guestCart');
    }
  };

  const getProductQuantity = (productId: string): number => {
    const item = cartItems.find((item) => item.product_id === productId);
    return item ? item.quantity : 0;
  };

  useEffect(() => {
    refreshCart();
  }, []);

  const value: GuestCartContextType = {
    cartItems,
    cartCount,
    refreshCart,
    getProductQuantity,
  };

  return (
    <GuestCartContext.Provider value={value}>
      {children}
    </GuestCartContext.Provider>
  );
};
